import Konva from 'konva';

import HaspObject from './HaspObject';
import HaspButton from './HaspButton';
import HaspMsgboxButton from './HaspMsgboxButton';
import { resolveOutlineGeometry } from '../draw/outlineGeometry';
import { resolveShadowGeometry } from '../draw/shadowGeometry';

export default class HaspMsgbox extends HaspObject {

    listOptions = [];
    buttonAreaHeight = 50;
    padding = 6;
    margin = 10;

    constructor(config) {
        const pageW = config._pageWidth  ?? null;
        const pageH = config._pageHeight ?? null;

        // Determine width first so text measurement uses the right wrap width
        config.width ??= pageW ?? 240;

        // Auto-calculate height from text content + button row when not explicitly set
        if (config.height === undefined) {
            const textW    = config.width - 2 * 10; // margin = 10
            const fontSize = config.text_font || 32;
            const tempText = new Konva.Text({
                text: config.text || '',
                fontSize,
                fontFamily: 'Roboto Condensed',
                width: Math.max(1, textW),
                wrap: 'word',
            });
            const measuredTextH = Math.max(fontSize, tempText.height());
            tempText.destroy();
            config.height = measuredTextH + 50 + 2 * 10; // buttonAreaHeight + 2*margin
        }

        config.x ??= 0;
        config.y ??= pageH !== null ? Math.round((pageH - config.height) / 2) : 0;

        super(config);

        // Force horizontal-only resize (width only, no x/y/h handles)
        this.stretch_mode = 'h_stretch';

        // Restore listOptions from config.options (class field reset after super)
        const raw = config.options;
        if (Array.isArray(raw) && raw.length > 0) {
            this.listOptions = raw.filter(o => typeof o === 'string');
        }
        if (this.listOptions.length === 0) this.listOptions = ['OK'];

        // Separator line between text area and button area.
        // Hidden by default (items.border_width = 0); visible when user sets it.
        const itemsStyle = this.style('items');
        this.separatorLine = new Konva.Line({
            points: this._separatorPoints(),
            stroke: itemsStyle.border_color || '#606060',
            strokeWidth: itemsStyle.border_width || 0,
            listening: false,
        });
        this.add(this.separatorLine);

        // Message text
        const mainStyle = this.style('main');
        this.textObj = new Konva.Text({
            x: this.margin,
            y: this.margin,
            width:  this.rect.width()  - 2 * this.margin,
            height: this.rect.height() - this.buttonAreaHeight - 2 * this.margin,
            text: this.text || '',
            fontSize: mainStyle.text_font || 32,
            fontFamily: 'Roboto Condensed',
            fill: mainStyle.text_color || '#eeeeee',
            align: 'center',
            verticalAlign: 'middle',
            wrap: 'word',
            listening: false,
            draggable: false,
        });
        this.add(this.textObj);

        // Clip children to the rounded container shape
        this._updateClip();

        // Text events
        this.on('text_text', () => { if (this.textObj) this.textObj.text(this.text || ''); });
        this.onStyle('main', 'text_color', () => { if (this.textObj) this.textObj.fill(this.style('main').text_color); });
        this.onStyle('main', 'text_font', () => { if (this.textObj) this.textObj.fontSize(this.style('main').text_font); });

        // Radius change — re-clip and redraw buttons
        this.onStyle('main', 'radius', () => {
            this._updateClip();
            this._updateLayout();
            this.drawButtons();
        });

        // msgbox is the only widget that clips its OWN group (to keep children
        // inside the rounded container), so unlike every other widget its clip
        // would swallow its own outline. Re-clip when the outline changes — a
        // clipping correction only; the outline itself is still drawn by the
        // shared CustomRect.
        // …and the same applies to its own shadow, whose extent the clip has to
        // open up for (shadow_color is excluded: it changes no geometry).
        [
            'outline_width', 'outline_pad', 'outline_opa',
            'shadow_width', 'shadow_spread', 'shadow_ofs_x', 'shadow_ofs_y', 'shadow_opa',
        ].forEach(prop => {
            this.onStyle('main', prop, () => this._updateClip());
        });

        // Separator events (items part, suffix 30)
        this.onStyle('items', 'border_color', () => { this.separatorLine.stroke(this.style('items').border_color || '#606060'); });
        this.onStyle('items', 'border_width', () => { this.separatorLine.strokeWidth(Math.max(1, this.style('items').border_width || 0)); });

        // Item-buttons (suffix 40) style events
        ['bg_color', 'border_color', 'border_width', 'radius', 'text_color', 'text_font'].forEach(prop => {
            this.onStyle('item_buttons', prop, () => this.drawButtons());
        });

        // Width-only resize; re-centre on parent page after transform
        this.on('transform', () => {
            this._updateLayout();
            this.drawButtons();
        });
        this.on('transformend', () => {
            this._updateLayout();
            this.drawButtons();
            this._centreOnPage();
        });

        this.drawButtons();
    }

    // -------------------------------------------------------------------------
    // Container — intercepts 'btn' toolbar click → add new msgbox button
    // -------------------------------------------------------------------------

    addItem(item) {
        if (item instanceof HaspButton) {
            this.listOptions.push('btn');
            this.drawButtons();
            this.getLayer()?.layerCallback('button-drag-end', this);
        }
        return null;
    }

    // -------------------------------------------------------------------------
    // Layout helpers
    // -------------------------------------------------------------------------

    _separatorPoints() {
        const y = this.rect.height() - this.buttonAreaHeight;
        return [0, y, this.rect.width(), y];
    }

    /** Clip children to the rounded-rect shape so they never overflow the border. */
    _updateClip() {
        const rw = this.rect.width();
        const rh = this.rect.height();
        const main = this.style('main');
        const bw = (main.border_width || 0) / 2;
        // The outline lives entirely outside the container, so the clip has to
        // open up by exactly its extent or msgbox would clip away its own
        // outline. Children stay clipped as before — when no outline is visible
        // the geometry below is byte-identical to what it was.
        const outline = resolveOutlineGeometry({
            width: rw,
            height: rh,
            radius: main.radius,
            outlineWidth: main.outline_width,
            outlinePad: main.outline_pad,
            opa: main.outline_opa,
        });
        const off = bw + (outline.visible ? outline.overflow : 0);
        const r  = outline.visible
            ? Math.min(outline.radiusOuter + bw, (rw + off * 2) / 2, (rh + off * 2) / 2)
            : Math.min(main.radius || 0, (rw + bw * 2) / 2, (rh + bw * 2) / 2);

        // The shared LvglShadowShape is a child of this group as well, so it was
        // being clipped away entirely — the shadow lies OUTSIDE the container and
        // the part that isn't is hidden behind the background. It can't be folded
        // into the container box the way the outline is: shadow_ofs_x/y make the
        // shadow box asymmetric. So it becomes a SECOND subpath — canvas clip()
        // unions subpaths under the nonzero rule — leaving the container's own
        // rounded clip untouched wherever the shadow doesn't reach.
        const shadow = resolveShadowGeometry({
            width: rw,
            height: rh,
            radius: main.radius,
            shadowWidth: main.shadow_width,
            shadowSpread: main.shadow_spread,
            ofsX: main.shadow_ofs_x,
            ofsY: main.shadow_ofs_y,
            opa: main.shadow_opa,
        });

        this.clipFunc((ctx) => {
            ctx.beginPath();
            this._appendRoundedRect(ctx, -off, -off, rw + off * 2, rh + off * 2, r);
            if (shadow.visible) {
                const b = shadow.bounds;
                // bounds is core padded by the same blur extent on every side.
                const ext = (b.width - shadow.core.width) / 2;
                const br = Math.min(shadow.radius + ext, b.width / 2, b.height / 2);
                this._appendRoundedRect(ctx, b.x, b.y, b.width, b.height, Math.max(0, br));
            }
        });
    }

    /** One rounded-rect subpath, clockwise, on the current path. */
    _appendRoundedRect(ctx, x0, y0, w, h, r) {
        ctx.moveTo(x0 + r, y0);
        ctx.lineTo(x0 + w - r, y0);
        ctx.arcTo(x0 + w, y0,     x0 + w, y0 + r,     r);
        ctx.lineTo(x0 + w, y0 + h - r);
        ctx.arcTo(x0 + w, y0 + h, x0 + w - r, y0 + h, r);
        ctx.lineTo(x0 + r, y0 + h);
        ctx.arcTo(x0,     y0 + h, x0,     y0 + h - r, r);
        ctx.lineTo(x0,     y0 + r);
        ctx.arcTo(x0,     y0,     x0 + r, y0,          r);
        ctx.closePath();
    }

    setNewSize(size) {
        super.setNewSize(size);
        this._updateLayout();
        this.drawButtons();
    }

    _updateLayout() {
        const rw = this.rect.width();
        const rh = this.rect.height();
        this.separatorLine.points(this._separatorPoints());
        if (this.textObj) {
            this.textObj.width(rw - 2 * this.margin);
            this.textObj.height(rh - this.buttonAreaHeight - 2 * this.margin);
        }
        this._updateClip();
    }

    _centreOnPage() {
        const parent = this.getParent();
        if (parent && typeof parent.width === 'function') {
            this.x(Math.round((parent.width() - this.rect.width()) / 2));
        }
    }

    // -------------------------------------------------------------------------
    // Button management
    // -------------------------------------------------------------------------

    clearButtons() {
        const toDestroy = this.getChildren().filter(c => c instanceof HaspMsgboxButton);
        toDestroy.forEach(c => c.destroy());
    }

    drawButtons() {
        this.clearButtons();

        const rw    = this.rect.width();
        const rh    = this.rect.height();
        const count = this.listOptions.length || 1;
        const bw    = (rw - this.margin * (count + 1)) / count;
        const y     = rh - this.buttonAreaHeight + this.padding;
        const bh    = this.buttonAreaHeight - 2 * this.padding;

        const itemButtons = this.style('item_buttons');
        let x = this.margin;
        this.listOptions.forEach((label, index) => {
            const btn = new HaspMsgboxButton({
                x, y,
                width:        Math.max(1, bw),
                height:       bh,
                obj:          'msgbox_btn',
                text:         label,
                bg_color:     itemButtons.bg_color,
                bg_opa:       itemButtons.bg_opa ?? 255,
                border_color: itemButtons.border_color,
                border_width: itemButtons.border_width ?? 1,
                radius:       itemButtons.radius ?? 5,
                fontSize:     itemButtons.text_font || 32,
                textFill:     itemButtons.text_color || '#eeeeee',
                theme:        this.theme,
                index,
            });
            btn.parentMsgbox = this;
            btn.on('dragmove', (e) => this._handleButtonDragMove(e));
            btn.on('dragend',  (e) => this._handleButtonDragEnd(e));
            this.add(btn);
            x += bw + this.margin;
        });

        if (this.idTextObj) this.idTextObj.moveToTop();
    }

    _handleButtonDragMove(e) {
        const btn = e.target;
        btn.moveToTop();
        if (btn.x() <= -20) {
            btn.rect.fill('#cc0000');
            btn.rect.opacity(1);
        } else {
            btn.rect.fill(this.rect.fill());
            btn.rect.opacity(1);
        }
    }

    _handleButtonDragEnd(e) {
        const btn     = e.target;
        const fromIdx = btn.attrs.index;

        if (btn.x() <= -20) {
            if (this.listOptions.length > 1) {
                this.listOptions.splice(fromIdx, 1);
            }
        } else {
            const rw    = this.rect.width();
            const count = this.listOptions.length;
            const bw    = (rw - this.margin * (count + 1)) / count;
            const droppedCX = btn.x() + btn.width() / 2;

            let toIdx = fromIdx, minDist = Infinity;
            for (let i = 0; i < count; i++) {
                const slotCX = this.margin + i * (bw + this.margin) + bw / 2;
                const dist = Math.abs(droppedCX - slotCX);
                if (dist < minDist) { minDist = dist; toIdx = i; }
            }

            if (toIdx !== fromIdx) {
                const label = this.listOptions.splice(fromIdx, 1)[0];
                this.listOptions.splice(toIdx, 0, label);
            }
        }

        this.drawButtons();
        this.getLayer()?.layerCallback('button-drag-end', this);
    }

    buttonTextChange(button) {
        const idx = button.attrs.index;
        if (idx !== undefined && idx < this.listOptions.length) {
            this.listOptions[idx] = button.text || '';
        }
    }

    // -------------------------------------------------------------------------
    // Export
    // -------------------------------------------------------------------------

    objectExport(page, objectData) {
        delete objectData.x;
        delete objectData.y;
        delete objectData.h;
        if (this.text) objectData.text = this.text;
        objectData.options = [...this.listOptions];
    }
}
